// src/App.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import { Role } from './types/enums';
import { InventoryItem, Vendor, User } from './types/interfaces';
import Production from './components/Production';
import BrewLog from './components/BrewLog';
import Inventory from './components/Inventory';
import Processing from './components/Processing';
import SalesDashboard from './components/SalesDashboard';
import Users from './components/Users';
import UserDetails from './components/UserDetails';
import Reporting from './components/Reporting';
import ReceivePage from './components/ReceivePage';
import Items from './components/Items';
import ItemDetails from './components/ItemDetails';
import Vendors from './components/Vendors';
import VendorDetails from './components/VendorDetails';
import PurchaseOrderForm from './components/PurchaseOrderForm';
import PurchaseOrderList from './components/PurchaseOrderList';
import Products from './components/Products';
import ProductDetails from './components/ProductDetails';
import InventoryItemDetails from './components/InventoryItemDetails';
import Locations from './components/Locations';
import Sites from './components/Sites';
import FacilityDesigner from './components/FacilityDesigner';
import FacilityView from './components/FacilityView';
import EquipmentPage from './components/Equipment';
import Login from './components/Login';
import { fetchInventory, fetchVendors } from './utils/fetchUtils';
import { exportToExcel } from './utils/excelUtils';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import BatchDetails from './components/BatchDetails';
import SalesOrderComponent from './components/SalesOrder';
import InvoiceComponent from './components/Invoice';
import BillOfLading from './components/BillOfLading';
import InBondTransferInvoice from './components/InBondTransferInvoice';
import MonthlyInventoryCountList, {
  MonthlyInventoryCountDetail,
} from './components/MonthlyInventoryCount';
import SalesOrderList from './components/SalesOrderList';
import InvoicesList from './components/InvoicesList';
import Customers from './components/Customers';
import CustomerDetails from './components/CustomerDetails';
import Settings from './components/Settings';
import QuickBooksSettings from './components/quickbooks/QuickBooksSettings';
import PackageTypes from './components/PackageTypes';
import ItemAliases from './components/ItemAliases';
import DbSnapshots from './components/DbSnapshots';
import KegTracking from './components/KegTracking';
import KegReturnsList, { KegReturnDetail } from './components/KegReturns';
import TaproomKegCountList, { TaproomKegCountDetail } from './components/TaproomKegCount';
import TransfersList, { TransferDetail } from './components/Transfers';
import TaproomOrdersList, { TaproomOrderDetail } from './components/TaproomOrders';
import SpiritsDashboard from './components/SpiritsDashboard';
import { SpiritsBottleOnHand, SpiritsBottleCountList, SpiritsBottleCountDetail } from './components/SpiritsBottles';
import { SpiritsTaproomOrderList, SpiritsTaproomOrderDetail, SpiritsTaproomReport } from './components/SpiritsTaproomOrders';
import SpiritsTtbReports from './components/SpiritsTtbReports';
import PosAdmin from './components/PosAdmin';
import PosMenuAdmin from './components/PosMenuAdmin';
import PosFloorAdmin from './components/PosFloorAdmin';
import PosTicketsAdmin from './components/PosTicketsAdmin';
import { isLiquorModuleEnabled, isPosModuleEnabled } from './config/featureFlags';
import {
  apiFetch,
  getAuthToken,
  clearAuth,
  AUTH_USER_KEY,
  ApiError,
  startAuthSessionWatch,
  ensureFreshToken,
} from './utils/api';
import NewVersionBanner from './components/NewVersionBanner';
import { useEdgeSwipeMenu } from './hooks/useEdgeSwipeMenu';

const ProductionPage: React.FC = () => <div className="page-container container"><h2>Production</h2><p>Production page coming soon</p></div>;

/** Shown when a non-admin opens a Management URL directly. */
const AdminOnly: React.FC = () => (
  <div className="page-container container">
    <h2>Access Denied</h2>
    <p>Only Admins can access this page</p>
  </div>
);

const AppContent: React.FC = () => {
  const [activeSection, setActiveSection] = useState('Home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showInventorySubmenu, setShowInventorySubmenu] = useState(false);
  const [showProductionSubmenu, setShowProductionSubmenu] = useState(false);
  const [showManagementSubmenu, setShowManagementSubmenu] = useState(false);
  const [showSalesSubmenu, setShowSalesSubmenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  // Set when a navigation is triggered by expanding/collapsing a submenu group
  // (or "Back"), so the route-change safety net below keeps the drawer open.
  const keepMenuOpenOnNavRef = useRef(false);

  // Leaf link clicked: close the drawer (backdrop goes with it).
  const closeMenu = () => {
    // Clear any pending "keep open" from a submenu toggle that navigated to the
    // page we're already on (no route change consumed it), so it can't leak.
    keepMenuOpenOnNavRef.current = false;
    setMenuOpen(false);
  };

  // Safety net: any route change closes the drawer unless it came from a submenu toggle.
  useEffect(() => {
    if (keepMenuOpenOnNavRef.current) {
      keepMenuOpenOnNavRef.current = false;
      return;
    }
    setMenuOpen(false);
  }, [location.pathname]);

  const [currentUser, setCurrentUser] = useState<User | null>(
    (() => {
      const userData = localStorage.getItem(AUTH_USER_KEY);
      try {
        return userData ? JSON.parse(userData) : null;
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
        clearAuth();
        return null;
      }
    })()
  );
  const [isAuthenticated, setIsAuthenticated] = useState(!!currentUser?.email && !!getAuthToken());
  const isAdmin = currentUser && [Role.SuperAdmin, Role.Admin].includes(currentUser.role as Role);
  const isSuperAdmin = currentUser && currentUser.role === Role.SuperAdmin;

  // Phone/touch widths: edge swipe opens/closes the drawer (hamburger still works).
  useEdgeSwipeMenu({
    menuOpen,
    setMenuOpen,
    enabled: isAuthenticated && !isLoading,
  });

  const refreshToken = async () => {
    console.log('[App] Checking token validity');
    if (!getAuthToken()) {
      console.log('[App] No token, redirecting to login');
      setCurrentUser(null);
      setIsAuthenticated(false);
      navigate('/login');
      return false;
    }
    try {
      await ensureFreshToken();
      // /api/me works for every role; /api/users is admin-only and used to
      // bounce role=User back to the login page.
      const response = await apiFetch('/api/me', {
        headers: { 'Content-Type': 'application/json' },
      });
      console.log('[App] Token check response:', { status: response.status, ok: response.ok });
      if (!response.ok) {
        throw new ApiError(`HTTP error: ${response.status}`, response.status);
      }
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      // Only clear the session on real auth failure. Network / 5xx blips
      // (e.g. Render cold start) must not wipe a still-valid JWT mid-task.
      const status = error instanceof ApiError ? error.status : 0;
      if (status === 401) {
        console.error('[App] Token validation error:', error);
        clearAuth();
        setCurrentUser(null);
        setIsAuthenticated(false);
        navigate('/login');
        return false;
      }
      console.error('[App] Token validation error (keeping session):', error);
      setIsAuthenticated(true);
      return true;
    }
  };

  const refreshInventory = useCallback(async () => {
    try {
      const data = await fetchInventory();
      console.log('[App] fetchInventory response:', {
        data,
        isArray: Array.isArray(data),
        length: Array.isArray(data) ? data.length : 'N/A',
        items: Array.isArray(data) ? data.map((item: InventoryItem) => ({
          identifier: item.identifier,
          status: item.status,
          type: item.type,
        })) : [],
      });
      if (Array.isArray(data)) {
        setInventory(data);
      } else {
        console.error('[App] Invalid inventory data format:', data);
        setInventory([]);
      }
    } catch (error: unknown) {
      console.error('[App] refreshInventory error:', error);
      setInventory([]);
      throw error;
    }
  }, []);

  const refreshVendors = useCallback(async () => {
    try {
      const data = await fetchVendors();
      console.log('[App] fetchVendors response:', {
        data,
        isArray: Array.isArray(data),
        length: Array.isArray(data) ? data.length : 'N/A',
      });
      setVendors(Array.isArray(data) ? data : []);
    } catch (error: unknown) {
      console.error('[App] Fetch vendors error:', error);
      setVendors([]);
    }
  }, []);

  useEffect(() => {
    startAuthSessionWatch();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      const token = getAuthToken();
      const userData = localStorage.getItem(AUTH_USER_KEY);
      let user = null;
      try {
        user = userData ? JSON.parse(userData) : null;
      } catch (e) {
        console.error('[App] Error parsing user from localStorage:', e);
        clearAuth();
      }
      if (user && token) {
        console.log('[App] User and token found, validating');
        const isValid = await refreshToken();
        if (isValid) {
          setCurrentUser(user);
          setIsAuthenticated(true);
          try {
            await Promise.all([refreshInventory(), refreshVendors()]);
          } catch (error) {
            console.error('[App] Initial data fetch error:', error);
          }
          if (location.pathname === '/login') {
            navigate('/', { replace: true });
          }
        }
      } else {
        console.log('[App] No user or token, redirecting to login');
        setCurrentUser(null);
        setIsAuthenticated(false);
        navigate('/login');
      }
      setIsLoading(false);
    };
    loadData();
  }, [location.pathname, navigate]);

  useEffect(() => {
    // pointerdown covers mouse + touch (Android Chrome) for dismiss-outside
    const handleOutsidePointer = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        menuOpen &&
        menuRef.current &&
        target &&
        !menuRef.current.contains(target) &&
        !target.closest('.hamburger')
      ) {
        console.log('[App] Closing menu due to outside pointer');
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [menuOpen]);

  const handleBackClick = () => {
    keepMenuOpenOnNavRef.current = true;
    console.log('[App] Back clicked');
    setShowInventorySubmenu(false);
    setShowProductionSubmenu(false);
    setShowManagementSubmenu(false);
    setShowSalesSubmenu(false);
    setActiveSection('Home');
    navigate('/');
  };

  const handleInventoryClick = () => {
    // Only arm keep-open if the toggle's navigation will change the route;
    // otherwise nothing consumes the flag (e.g. already on /inventory).
    keepMenuOpenOnNavRef.current =
      location.pathname !== (activeSection === 'Inventory' && showInventorySubmenu ? '/' : '/inventory');
    console.log('[App] Inventory menu clicked');
    if (activeSection === 'Inventory' && showInventorySubmenu) {
      setShowInventorySubmenu(false);
      setActiveSection('Home');
      navigate('/');
    } else {
      setActiveSection('Inventory');
      setShowInventorySubmenu(true);
      navigate('/inventory');
    }
  };

  const handleProductionClick = () => {
    keepMenuOpenOnNavRef.current = true;
    console.log('[App] Production menu clicked');
    if (activeSection === 'Production' && showProductionSubmenu) {
      setShowProductionSubmenu(false);
      setActiveSection('Home');
      navigate('/');
    } else {
      setActiveSection('Production');
      setShowProductionSubmenu(true);
      navigate('/production');
    }
  };

  const handleManagementClick = () => {
    keepMenuOpenOnNavRef.current = true;
    console.log('[App] Management menu clicked');
    if (activeSection === 'Management' && showManagementSubmenu) {
      setShowManagementSubmenu(false);
      setActiveSection('Home');
      navigate('/');
    } else {
      setActiveSection('Management');
      setShowManagementSubmenu(true);
      navigate('/management');
    }
  };

  const handleSalesClick = () => {
    keepMenuOpenOnNavRef.current = true;
    console.log('[App] Sales & Distribution menu clicked');
    if (activeSection === 'Sales & Distribution' && showSalesSubmenu) {
      setShowSalesSubmenu(false);
      setActiveSection('Home');
      navigate('/');
    } else {
      setActiveSection('Sales & Distribution');
      setShowSalesSubmenu(true);
      navigate('/sales-dashboard');
    }
  };

  const handleLogOff = () => {
    console.log('[App] Log Off clicked');
    clearAuth();
    setCurrentUser(null);
    setIsAuthenticated(false);
    setActiveSection('Home');
    setShowManagementSubmenu(false);
    setShowInventorySubmenu(false);
    setShowProductionSubmenu(false);
    setShowSalesSubmenu(false);
    navigate('/login');
    setMenuOpen(false);
  };

  if (!isAuthenticated) {
    return <Login />;
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <img src="/tilly-logo.png" alt="Tilly Logo" className="loading-logo" />
        <h1 className="loading-title">Tilly</h1>
        <p className="loading-subtitle">Powered by Paws & Pours</p>
      </div>
    );
  }

  return (
    <div className={`App${menuOpen ? ' menu-open' : ''}`}>
      <NewVersionBanner />
      <button
        type="button"
        className="hamburger"
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        onClick={() => {
          console.log('[App] Hamburger clicked, menuOpen:', !menuOpen);
          setMenuOpen(!menuOpen);
        }}
      >
        ☰
      </button>
      {menuOpen && (
        <button
          type="button"
          className="menu-backdrop"
          aria-label="Close menu"
          onPointerDown={(e) => {
            e.preventDefault();
            setMenuOpen(false);
          }}
        />
      )}
      <nav className={`menu ${menuOpen ? 'open' : ''}`} ref={menuRef}>
        <ul>
          {showInventorySubmenu ? (
            [
              { name: 'Back', action: handleBackClick },
              { name: 'Vendors', path: '/vendors' },
              { name: 'Receive Inventory', path: '/receive' },
              { name: 'Transfers', path: '/transfers' },
              { name: 'Inventory', path: '/inventory' },
              { name: 'Monthly Inventory Count', path: '/monthly-inventory-counts' },
              { name: 'Items', path: '/items' },
            ].map((item) => (
              <li key={item.name}>
                {item.path ? (
                  <Link
                    to={item.path}
                    onClick={() => {
                      console.log('[App] Submenu item clicked:', item.name);
                      closeMenu();
                      setActiveSection(item.name === 'Receive Inventory' ? 'Inventory' : item.name);
                    }}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <button onClick={item.action}>{item.name}</button>
                )}
              </li>
            ))
          ) : showProductionSubmenu ? (
            [
              { name: 'Back', action: handleBackClick },
              { name: 'Planning', path: '/planning', disabled: true },
              { name: 'Products', path: '/products' },
              { name: 'Facility View', path: '/facility-view' },
              { name: 'Production', path: '/production' },
              { name: 'Equipment', path: '/equipment' },
            ].map((item) => (
              <li key={item.name}>
                {item.disabled ? (
                  <span className="disabled">{item.name}</span>
                ) : item.path ? (
                  <Link
                    to={item.path}
                    onClick={() => {
                      console.log('[App] Submenu item clicked:', item.name);
                      closeMenu();
                      setActiveSection(item.name);
                    }}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <button onClick={item.action}>{item.name}</button>
                )}
              </li>
            ))
          ) : showManagementSubmenu && isAdmin ? (
            [
              { name: 'Back', action: handleBackClick },
              { name: 'Locations', path: '/locations' },
              { name: 'Sites', path: '/sites' },
              { name: 'Facility Designer', path: '/facility-designer' },
              { name: 'Package Types', path: '/package-types' },
              ...(isPosModuleEnabled()
                ? [
                    { name: 'POS Staff & Devices', path: '/pos-admin' },
                    { name: 'POS Menus', path: '/pos-menus' },
                    { name: 'POS Floor', path: '/pos-floor' },
                    { name: 'POS Tickets', path: '/pos-tickets' },
                  ]
                : []),
              ...(isSuperAdmin
                ? [
                    { name: 'Item Aliases', path: '/item-aliases' },
                    { name: 'DB Snapshots', path: '/db-snapshots' },
                  ]
                : []),
              { name: 'Settings', path: '/settings' },
              { name: 'Log Off', action: handleLogOff },
            ].map((item) => (
              <li key={item.name}>
                {item.path ? (
                  <Link
                    to={item.path}
                    onClick={() => {
                      console.log('[App] Submenu item clicked:', item.name);
                      closeMenu();
                      setActiveSection(item.name);
                    }}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <button onClick={item.action}>{item.name}</button>
                )}
              </li>
            ))
          ) : showSalesSubmenu ? (
            [
              { name: 'Back', action: handleBackClick },
              { name: 'Sales Dashboard', path: '/sales-dashboard' },
              { name: 'Sales Orders', path: '/sales-orders' },
              { name: 'Taproom', path: '/taproom-orders' },
              { name: 'Invoices', path: '/invoices' },
              { name: 'Customers', path: '/customers' },
              { name: 'Keg Tracking and Management', path: '/keg-tracking' },
              { name: 'Keg Returns', path: '/keg-returns' },
              { name: 'Taproom Keg Count', path: '/taproom-keg-counts' },
            ].map((item) => (
              <li key={item.name}>
                {item.path ? (
                  <Link
                    to={item.path}
                    onClick={() => {
                      console.log('[App] Submenu item clicked:', item.name);
                      closeMenu();
                      setActiveSection('Sales & Distribution');
                    }}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <button onClick={item.action}>{item.name}</button>
                )}
              </li>
            ))
          ) : (
            [
              { name: 'Home', subMenu: null },
              { name: 'Production', subMenu: true },
              ...(isLiquorModuleEnabled() ? [{ name: 'Spirits', subMenu: null }] : []),
              { name: 'Inventory', subMenu: true },
              { name: 'Sales & Distribution', subMenu: true },
              ...(isAdmin ? [{ name: 'Users', subMenu: null }] : []),
              { name: 'Reporting', subMenu: null },
              ...(isAdmin ? [{ name: 'Management', subMenu: true }] : []),
            ].map((section) => (
              <li key={section.name}>
                <button
                  onClick={() => {
                    console.log('[App] Main menu item clicked:', section.name);
                    if (section.name === 'Production') {
                      handleProductionClick();
                    } else if (section.name === 'Inventory') {
                      handleInventoryClick();
                    } else if (section.name === 'Management') {
                      handleManagementClick();
                    } else if (section.name === 'Sales & Distribution') {
                      handleSalesClick();
                    } else if (section.name === 'Spirits') {
                      setActiveSection('Spirits');
                      setShowInventorySubmenu(false);
                      setShowProductionSubmenu(false);
                      setShowManagementSubmenu(false);
                      setShowSalesSubmenu(false);
                      closeMenu();
                      navigate('/spirits');
                    } else {
                      setActiveSection(section.name);
                      setShowInventorySubmenu(false);
                      setShowProductionSubmenu(false);
                      setShowManagementSubmenu(false);
                      setShowSalesSubmenu(false);
                      closeMenu();
                      navigate(section.name === 'Home' ? '/' : `/${section.name.toLowerCase().replace(' & ', '-')}`);
                    }
                  }}
                  className={activeSection === section.name ? 'active' : ''}
                >
                  {section.name}
                </button>
              </li>
            ))
          )}
        </ul>
      </nav>
      <div className="content">
        <Routes>
          <Route
            path="/"
            element={
              <>
                {activeSection === 'Home' && <FacilityView />}
                {/* Inventory mounts only via /inventory route — not here — so opening the
                    Inventory submenu does not flash a second mount before navigation. */}
                {activeSection === 'Production' && location.pathname === '/production' && (
                  <Production inventory={inventory} refreshInventory={refreshInventory} />
                )}
                {activeSection === 'Products' && <Products />}
                {activeSection === 'Production' && location.pathname === '/production-page' && <ProductionPage />}
                {activeSection === 'Locations' && isAdmin && <Locations />}
                {activeSection === 'Equipment' && <EquipmentPage />}
                {activeSection === 'Planning' && <div className="page-container container"><h2>Planning</h2><p>Coming soon</p></div>}
                {activeSection === 'Facility Designer' && isAdmin && <FacilityDesigner />}
                {activeSection === 'Facility View' && <FacilityView />}
                {activeSection === 'Vendors' && <Vendors vendors={vendors} refreshVendors={refreshVendors} />}
                {activeSection === 'Transfers' && location.pathname === '/transfers' && <TransfersList />}
                {activeSection === 'Processing' && <Processing inventory={inventory} refreshInventory={refreshInventory} />}
                {activeSection === 'Users' && isAdmin && <Users />}
                {activeSection === 'Reporting' && <Reporting exportToExcel={exportToExcel} />}
                {activeSection === 'Management' && isAdmin && location.pathname === '/management' && <div className="page-container container"><h2>Management</h2><p>Select an option from the menu</p></div>}
                {activeSection === 'Package Types' && isAdmin && <PackageTypes />}
                {activeSection === 'Item Aliases' && isSuperAdmin && <ItemAliases />}
                {activeSection === 'DB Snapshots' && isSuperAdmin && <DbSnapshots />}
              </>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/inventory" element={
            <Inventory
              vendors={vendors}
              refreshVendors={refreshVendors}
              inventory={inventory}
              refreshInventory={refreshInventory}
            />
          } />
          {/* Splat routes: item/inventory keys may contain `/` (e.g. "1/6 BBL Keg") */}
          <Route path="/inventory/*" element={<InventoryItemDetails inventory={inventory} refreshInventory={refreshInventory} />} />
          <Route path="/monthly-inventory-counts" element={<MonthlyInventoryCountList />} />
          <Route path="/monthly-inventory-counts/:countId" element={<MonthlyInventoryCountDetail />} />
          <Route path="/transfers" element={<TransfersList />} />
          <Route path="/transfers/:transferId/in-bond-invoice" element={<InBondTransferInvoice />} />
          <Route path="/transfers/:transferId" element={<TransferDetail />} />
          <Route path="/items" element={<Items />} />
          <Route path="/items/*" element={<ItemDetails />} />
          <Route path="/sites" element={isAdmin ? <Sites /> : <div className="page-container container"><h2>Access Denied</h2><p>Only Admins can access this page</p></div>} />
          <Route path="/receive" element={<ReceivePage refreshInventory={refreshInventory} vendors={vendors} refreshVendors={refreshVendors} />} />
          <Route path="/vendors" element={<Vendors vendors={vendors} refreshVendors={refreshVendors} />} />
          <Route path="/vendors/:name" element={<VendorDetails vendors={vendors} refreshVendors={refreshVendors} refreshInventory={refreshInventory} />} />
          <Route path="/vendors/:name/purchase-orders" element={<PurchaseOrderList />} />
          <Route path="/vendors/:name/purchase-orders/new" element={<PurchaseOrderForm />} />
          <Route path="/vendors/:name/purchase-orders/:poNumber" element={<PurchaseOrderForm />} />
          <Route path="/processing" element={<Processing inventory={inventory} refreshInventory={refreshInventory} />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetails />} />
          <Route path="/production" element={<Production inventory={inventory} refreshInventory={refreshInventory} />} />
          <Route path="/production/:batchId" element={<BatchDetails inventory={inventory} refreshInventory={refreshInventory} />} />
          <Route path="/production/:batchId/brewlog" element={<BrewLog />} />
          <Route path="/production-page" element={<ProductionPage />} />
          <Route path="/locations" element={isAdmin ? <Locations /> : <div className="page-container container"><h2>Access Denied</h2><p>Only Admins can access this page</p></div>} />
          <Route path="/equipment" element={<EquipmentPage />} />
          <Route path="/spirits" element={<SpiritsDashboard />} />
          {isLiquorModuleEnabled() && (
            <>
              <Route path="/spirits/bottles" element={<SpiritsBottleOnHand />} />
              <Route path="/spirits/bottle-counts" element={<SpiritsBottleCountList />} />
              <Route path="/spirits/bottle-counts/:countId" element={<SpiritsBottleCountDetail />} />
              <Route path="/spirits/taproom-orders" element={<SpiritsTaproomOrderList />} />
              <Route path="/spirits/taproom-orders/:orderId" element={<SpiritsTaproomOrderDetail />} />
              <Route path="/spirits/taproom-report" element={<SpiritsTaproomReport />} />
              <Route path="/spirits/ttb-reports" element={<SpiritsTtbReports />} />
            </>
          )}
          <Route path="/planning" element={<div className="page-container container"><h2>Planning</h2><p>Coming soon</p></div>} />
          <Route path="/facility-designer" element={isAdmin ? <FacilityDesigner /> : <div className="page-container container"><h2>Access Denied</h2><p>Only Admins can access this page</p></div>} />
          <Route path="/facility-view" element={<FacilityView />} />
          <Route path="/management" element={isAdmin ? <div className="page-container container"><h2>Management</h2><p>Select an option from the menu</p></div> : <div className="page-container container"><h2>Access Denied</h2><p>Only Admins can access this page</p></div>} />
          <Route path="/users" element={isAdmin ? <Users /> : <AdminOnly />} />
          <Route path="/users/:email" element={isAdmin ? <UserDetails /> : <AdminOnly />} />
          <Route path="/reporting" element={<Reporting exportToExcel={exportToExcel} />} />
          <Route path="/reporting/*" element={<Reporting exportToExcel={exportToExcel} />} />
          <Route path="/sales-dashboard" element={<SalesDashboard />} />
          <Route path="/sales-orders" element={<SalesOrderList />} />
          <Route path="/sales-orders/new" element={<SalesOrderComponent inventory={inventory} />} />
          <Route path="/sales-orders/:orderId" element={<SalesOrderComponent inventory={inventory} />} />
          <Route path="/taproom-orders" element={<TaproomOrdersList />} />
          <Route path="/taproom-orders/:orderId" element={<TaproomOrderDetail />} />
          <Route path="/invoices" element={<InvoicesList />} />
          <Route path="/invoices/:invoiceId" element={<InvoiceComponent inventory={inventory} refreshInventory={refreshInventory} />} />
          <Route path="/invoices/:invoiceId/bol" element={<BillOfLading />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/customers/:customerId" element={<CustomerDetails />} />
          <Route path="/keg-tracking" element={<KegTracking inventory={inventory} refreshInventory={refreshInventory} />} />
          <Route path="/keg-returns" element={<KegReturnsList />} />
          <Route path="/keg-returns/:returnId" element={<KegReturnDetail />} />
          <Route path="/taproom-keg-counts" element={<TaproomKegCountList />} />
          <Route path="/taproom-keg-counts/:countId" element={<TaproomKegCountDetail />} />
          <Route path="/settings" element={isAdmin ? <Settings /> : <AdminOnly />} />
          <Route path="/settings/quickbooks" element={isAdmin ? <QuickBooksSettings /> : <AdminOnly />} />
          <Route
            path="/package-types"
            element={
              isAdmin ? (
                <PackageTypes />
              ) : (
                <div className="page-container container">
                  <h2>Access Denied</h2>
                  <p>Only Admins can manage package types</p>
                </div>
              )
            }
          />
          <Route
            path="/pos-admin"
            element={
              isAdmin ? (
                <PosAdmin />
              ) : (
                <div className="page-container container">
                  <h2>Access Denied</h2>
                  <p>Only Admins can manage POS staff and devices</p>
                </div>
              )
            }
          />
          <Route
            path="/pos-menus"
            element={
              isAdmin ? (
                <PosMenuAdmin />
              ) : (
                <div className="page-container container">
                  <h2>Access Denied</h2>
                  <p>Only Admins can manage POS menus</p>
                </div>
              )
            }
          />
          <Route
            path="/pos-floor"
            element={
              isAdmin ? (
                <PosFloorAdmin />
              ) : (
                <div className="page-container container">
                  <h2>Access Denied</h2>
                  <p>Only Admins can manage POS floor layout</p>
                </div>
              )
            }
          />
          <Route
            path="/pos-tickets"
            element={
              isAdmin ? (
                <PosTicketsAdmin />
              ) : (
                <div className="page-container container">
                  <h2>Access Denied</h2>
                  <p>Only Admins can view POS tickets</p>
                </div>
              )
            }
          />
          <Route
            path="/item-aliases"
            element={
              isSuperAdmin ? (
                <ItemAliases />
              ) : (
                <div className="page-container container">
                  <h2>Access Denied</h2>
                  <p>Only SuperAdmin can manage item aliases</p>
                </div>
              )
            }
          />
          <Route
            path="/db-snapshots"
            element={
              isSuperAdmin ? (
                <DbSnapshots />
              ) : (
                <div className="page-container container">
                  <h2>Access Denied</h2>
                  <p>Only SuperAdmin can manage DB snapshots</p>
                </div>
              )
            }
          />
        </Routes>
      </div>
      <nav className="navbar-mobile">
        <Link to="/keg-tracking" onClick={() => {
          console.log('[App] Navbar-mobile Kegs clicked');
          setMenuOpen(false);
        }}>Kegs</Link>
        <Link to="/sales-orders" onClick={() => {
          console.log('[App] Navbar-mobile Sales Orders clicked');
          setMenuOpen(false);
        }}>Sales Orders</Link>
        <Link to="/taproom-orders" onClick={() => {
          console.log('[App] Navbar-mobile Taproom clicked');
          setMenuOpen(false);
        }}>Taproom</Link>
        <Link to="/invoices" onClick={() => {
          console.log('[App] Navbar-mobile Invoices clicked');
          setMenuOpen(false);
        }}>Invoices</Link>
        <Link to="/production" onClick={() => {
          console.log('[App] Navbar-mobile Batches clicked');
          setMenuOpen(false);
        }}>Batches</Link>
      </nav>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;